import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent, type ReactNode, type CSSProperties } from 'react';
import { ApartmentOutlined, CameraOutlined, LineChartOutlined, LoadingOutlined, MinusSquareOutlined, PlusSquareOutlined } from '@ant-design/icons';
import { exportPng } from '../lib/exportImage.js';
import { ToolFan } from './ToolFan.js';
import type { ExecutivePositionCard, ExecutivePositionDept, ExecutivePositionsView } from '@shared/types';

/**
 * ผังผู้บริหารตามตำแหน่ง — การ์ดละหนึ่งอัตรา
 *
 *   ปลัด
 *    ├ กลุ่มภารกิจ (หัวกลุ่ม + รองปลัด) ─┬ ส่วนราชการ ─┬ รองหัวส่วนราชการ ─┬ ผอ. ─ ผอ. ─ … ─ ชช ─ ชช
 *    │                                  │             │                     (แถวเดียวกันทั้งหมด)
 *    │                                  │             └ ที่ปรึกษา
 *    │                                  └ ส่วนราชการ ...
 *    ├ ที่ปรึกษา ─ ราง: การ์ดรายคน         ┐
 *    ├ ผู้ตรวจราชการ ─ ราง: การ์ดรายคน     ├ ระดับเดียวกับรองปลัด ตามที่ผู้ใช้สั่ง (19 ก.ย. 2569)
 *    └ ผู้ช่วยปลัด ─ ราง: การ์ดรายคน       ┘
 *
 * หน้าตาตามแบบบนแคนวาส "ผังผู้บริหาร YANA — Modern" ที่ผู้ใช้อนุมัติ
 *
 * ⚠ วาดเป็น HTML ธรรมดา ไม่ใช่ React Flow — ผังนี้เป็นเอกสารที่เลื่อนอ่าน
 *   เส้นเชื่อมทั้งหมดเป็น CSS (::before / ::after) ใน styles.css หมวด .opv
 *
 * ⚠ ข้อมูลทั้งหมดประกอบที่เซิร์ฟเวอร์ (services/executivePositions.ts)
 *   หน้าจอไม่จัดกลุ่มเอง ไม่จับคู่รองปลัดกับกลุ่มภารกิจเอง
 *
 * ⚠ ตัวกรอง "ว่าง / ใกล้เกษียณ" ทำให้การ์ดที่ไม่ตรงจางลง ไม่ได้ตัดออก
 *   ถ้าตัดออก เส้นผังจะขาดเป็นช่วง ๆ และคอลัมน์จะหดจนอ่านไม่ออกว่าอยู่กรมไหน
 *
 * ────────────────────────────────────────────────────────────────
 *  กาง / ยุบ — แบบเดียวกับผังหน่วยงาน (ผู้ใช้สั่ง 19 ก.ย. 2569)
 * ────────────────────────────────────────────────────────────────
 *
 *   ปลัด        กาง/ยุบ ทั้ง 7 กิ่ง
 *   หัวกิ่ง       กาง/ยุบ ส่วนราชการในกลุ่ม (กิ่งขึ้นตรงปลัด: การ์ดรายคน)
 *   ส่วนราชการ   กาง/ยุบ การ์ดรายตำแหน่งใต้หัวส่วนราชการ
 *
 * เปิดมา: แสดง 2 ระดับ — ปลัดกับหัวกิ่ง ทุกกิ่งยุบไว้ (ผู้ใช้สั่ง 19 ก.ย. 2569)
 *
 * ⚠ ปุ่มกาง/ยุบบอกจำนวนอัตราว่างข้างในด้วยเสมอ — ยุบไว้แล้วอัตราว่างต้องไม่หายไปจากสายตา
 *   ผู้ใช้ที่กรอง "ว่าง" อยู่จะเห็นว่ากิ่งไหนมีของที่ต้องกางดู
 *
 * ────────────────────────────────────────────────────────────────
 *  ลาก / ซูม — แบบเดียวกับผังหน่วยงาน (ผู้ใช้สั่ง 19 ก.ย. 2569)
 * ────────────────────────────────────────────────────────────────
 *
 * ลากพื้นเพื่อเลื่อน · ล้อเมาส์หรือบีบนิ้วบนแทร็กแพดเพื่อซูมรอบตำแหน่งเมาส์ · ปุ่ม + ⌾ − มุมขวาล่าง
 * ใช้คลาส .flow-zoom กับ .flow-canvas ตัวเดียวกับผังหน่วยงาน หน้าตาจึงตรงกัน
 *
 * ⚠ ไม่ได้ย้ายไปใช้ React Flow — การ์ดสูงไม่เท่ากันและกาง/ยุบได้ ถ้าเป็นโหนดของ React Flow
 *   ต้องวัดความสูงทุกใบแล้วคำนวณพิกัดใหม่ทุกครั้งที่กาง ที่นี่ใช้ transform ครอบทั้งผังแทน
 *   เส้นเชื่อม (CSS) จึงยังตามการ์ดไปเองโดยไม่ต้องคำนวณอะไร
 *
 * ⚠ ลากได้จากทุกจุดรวมทั้งบนการ์ด (ผังแน่น พื้นว่างมีน้อย) — นับเป็นการลากเมื่อขยับเกิน DRAG_PX
 *   แล้วกลืนคลิกที่ตามมา (onClickCapture) กดปล่อยเฉย ๆ ยังเปิดการ์ด/กางกิ่งตามปกติ
 */

type Filter = 'all' | 'vacant' | 'retiring';

const STAFF_KEYS = ['staff:ที่ปรึกษา', 'staff:ผู้ตรวจราชการ', 'staff:ผู้ช่วยปลัด'];

/** สีของแต่ละกิ่ง — ชื่อคลาสผูกกับสีใน styles.css (.opv-tone-*) */
const TONE: Record<string, string> = {
  'กลุ่มภารกิจด้านทรัพย์สิน': 'teal',
  'กลุ่มภารกิจด้านรายได้': 'blue',
  'กลุ่มภารกิจด้านรายจ่ายและหนี้สิน': 'violet',
};

export function OrgPositions({
  data,
  onOpen,
  openAll = false,
  toolsExtra,
  onRetireWave,
  focusId = null,
  onUnits,
}: {
  data: ExecutivePositionsView;
  onOpen: (posId: string) => void;
  /** เปิดมากางทุกกิ่ง — ใช้กับภาพตรวจ (preview-positions EXPAND=1) หน้าจอจริงไม่ส่ง */
  openAll?: boolean;
  /** ปุ่มเพิ่มในชุดเครื่องมือ (สลับธีม · เต็มจอ) — ตัวครอบส่งมา แบบเดียวกับ OrgFlow */
  toolsExtra?: ReactNode;
  /** เปิดกราฟคลื่นเกษียณ 5 ปีของทั้งกระทรวง (เปิดเป็นแผงข้าง) */
  onRetireWave?: () => void;
  /** อัตราที่กำลังเปิดดูรายละเอียด — ผังเหลือเฉพาะสายของอัตรานี้ (ผู้ใช้สั่ง 19 ก.ย. 2569) · null = ผังเต็ม */
  focusId?: string | null;
  /** กลับไปผังหน่วยงาน (การ์ดกระทรวง → กลุ่มภารกิจ) — ยอดเป็นของบัญชีปลัด (ระดับ 9 ขึ้นไป) ตามที่เซิร์ฟเวอร์ตัดมา */
  onUnits?: () => void;
}) {
  const fy = data.fiscalYear;
  const { advisers, inspectors, assistants } = data.staff;
  const box = useRef<HTMLDivElement>(null);
  /* ไม่มีปุ่มกรองแล้ว — คงค่า 'all' ไว้ให้ dim() ใช้ ถ้าวันหน้าจะกลับมามีตัวกรองก็ต่อที่นี่ */
  const filter: Filter = 'all';

  const pan = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<View>({ x: 0, y: 0, k: 1 });
  const drag = useRef<{ id: number; x: number; y: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  /** จัดให้ทั้งผังอยู่ในจอ — ⚠ ไม่ย่อเล็กกว่า MIN_FIT เพราะการ์ดจะเล็กจนอ่านชื่อไม่ออก
      ถ้ากว้างเกิน จัดให้ปลัดอยู่กลางจอแล้วชิดบน ผู้ใช้ลากดูต่อเอง */
  const fit = useCallback(() => {
    const stage = box.current;
    const tree = pan.current;
    if (!stage || !tree) return;
    const cw = stage.clientWidth;
    const ch = stage.clientHeight;
    const w = tree.offsetWidth;
    const h = tree.offsetHeight;
    const k = clamp(Math.min(1, (cw - 48) / w, (ch - 48) / h), MIN_FIT, 1);
    setView({ k, x: (cw - w * k) / 2, y: h * k < ch ? (ch - h * k) / 2 : 24 });
  }, []);

  useLayoutEffect(() => {
    fit();
  }, [fit]);

  /** ซูมรอบจุด (px, py) ในพิกัดของกรอบ — จุดใต้เมาส์ต้องอยู่ที่เดิมหลังซูม */
  const zoomAt = useCallback((factor: number, px?: number, py?: number) => {
    const stage = box.current;
    if (!stage) return;
    const cx = px ?? stage.clientWidth / 2;
    const cy = py ?? stage.clientHeight / 2;
    setView((v) => {
      const k = clamp(v.k * factor, MIN_K, MAX_K);
      const r = k / v.k;
      return { k, x: cx - (cx - v.x) * r, y: cy - (cy - v.y) * r };
    });
  }, []);

  /* ⚠ ต้องผูกเองแบบ passive: false — onWheel ของ React เป็น passive จึงกัน preventDefault ไม่ได้
       แล้วหน้าเว็บทั้งหน้าจะเลื่อนตามไปด้วยขณะซูม */
  useEffect(() => {
    const stage = box.current;
    if (!stage) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = stage.getBoundingClientRect();
      /* แทร็กแพดสองนิ้ว (ไม่กด ctrl) = เลื่อน · ล้อเมาส์หรือบีบนิ้ว (ctrlKey) = ซูม — เหมือน React Flow */
      if (!e.ctrlKey && Math.abs(e.deltaX) > 0 && e.deltaMode === 0) {
        setView((v) => ({ ...v, x: v.x - e.deltaX, y: v.y - e.deltaY }));
        return;
      }
      zoomAt(Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0015)), e.clientX - r.left, e.clientY - r.top);
    };
    stage.addEventListener('wheel', onWheel, { passive: false });
    return () => stage.removeEventListener('wheel', onWheel);
  }, [zoomAt]);

  /* ลากได้ทุกที่ รวมทั้งบนการ์ด — เริ่มเลื่อนผังเมื่อขยับเกิน DRAG_PX เท่านั้น
     ถ้ากดแล้วปล่อยเฉย ๆ ยังเป็นการคลิกการ์ด/ปุ่มกางตามเดิม */
  const moved = useRef(false);
  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 || (e.target as HTMLElement).closest('.flow-zoom, .flow-tools')) return;
    drag.current = { id: e.pointerId, x: e.clientX, y: e.clientY };
    moved.current = false;
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (!moved.current) {
      if (Math.hypot(dx, dy) < DRAG_PX) return;
      moved.current = true;
      e.currentTarget.setPointerCapture(e.pointerId);
      setDragging(true);
    }
    d.x = e.clientX;
    d.y = e.clientY;
    setView((v) => ({ ...v, x: v.x + dx, y: v.y + dy }));
  };
  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (drag.current?.id === e.pointerId) {
      drag.current = null;
      setDragging(false);
    }
  };
  /* ลากเสร็จแล้วอย่าให้ปล่อยเมาส์บนการ์ดกลายเป็นคลิกเปิดแผง */
  const onClickCapture = (e: ReactMouseEvent) => {
    if (moved.current) {
      e.stopPropagation();
      e.preventDefault();
      moved.current = false;
    }
  };

  /*
   * บันทึกรูปทั้งผัง — ถ่ายตัวผัง (.opv) ที่ขนาดจริง ไม่ใช่กรอบที่เห็น
   * ⚠ ปลด transform ชั่วคราวระหว่างถ่าย ไม่งั้นได้รูปที่ถูกย่อ/เลื่อนตามที่ผู้ใช้ซูมไว้
   *   คืนค่าใน finally เสมอ · React ไม่เขียน style ทับเพราะค่า view ไม่ได้เปลี่ยน
   */
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const savePng = useCallback(async () => {
    const el = pan.current;
    if (!el || saving) return;
    setSaving(true);
    setSaveErr(null);
    const prev = el.style.transform;
    try {
      el.style.transform = 'none';
      await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
      const host = el.closest<HTMLElement>('.opv-layer') ?? el;
      await exportPng(el, 'ผังผู้บริหาร', { background: getComputedStyle(host).backgroundColor });
    } catch (err) {
      setSaveErr(err instanceof Error ? err.message : 'บันทึกรูปไม่สำเร็จ');
    } finally {
      el.style.transform = prev;
      setSaving(false);
    }
  }, [saving]);

  const all = useMemo(() => allCards(data), [data]);
  const stats = useMemo(
    () => ({
      total: all.length,
      vacant: all.filter((c) => c.vacant).length,
      thisYear: all.filter((c) => !c.vacant && c.retireFiscalYear === fy).length,
    }),
    [all, fy],
  );

  /* กิ่งที่กางอยู่ — ค่าเริ่มต้นดูหัวไฟล์ */
  const laneKeys = useMemo(() => data.lanes.map((l) => `lane:${l.missionGroup}`), [data]);
  const allKeys = useMemo(
    () => ['palad', ...laneKeys, ...data.lanes.flatMap((l) => l.departments.map((d) => `dept:${d.name}`)), ...STAFF_KEYS],
    [data, laneKeys],
  );
  /* ⚠ เปิดมาแสดง 2 ระดับ — ปลัด กับหัวกิ่ง (ผู้ใช้สั่ง 19 ก.ย. 2569) กดกางกิ่งเพื่อดูส่วนราชการ */
  const [open, setOpen] = useState<Set<string>>(() => new Set(openAll ? allKeys : ['palad']));
  const toggle = (k: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  /*
   * ────────────────────────────────────────────────────────────────
   *  โหมดดูเฉพาะสาย — เปิดรายละเอียดคนไหน ผังเหลือเฉพาะสายของคนนั้น
   * ────────────────────────────────────────────────────────────────
   * ปลัด → กิ่ง → ส่วนราชการ → รอง → ตัวคนนั้น · พี่น้องทุกชั้นซ่อน ลูกหลานของตัวคนนั้นยังตามสถานะกาง/ยุบเดิม
   * ⚠ ไม่แก้ `open` ของผู้ใช้ — กางบรรพบุรุษเพิ่มชั่วคราวผ่าน union ปิดแผงแล้วผังกลับมาเหมือนก่อนเปิดทุกอย่าง
   */
  const focus = useMemo(() => (focusId ? locate(data, focusId) : null), [data, focusId]);
  const shownOpen = useMemo(() => {
    if (!focus) return open;
    const u = new Set(open);
    u.add('palad');
    if (focus.lane) u.add(`lane:${focus.lane}`);
    if (focus.dept) u.add(`dept:${focus.dept}`);
    if (focus.staff) u.add(`staff:${focus.staff}`);
    return u;
  }, [open, focus]);
  const ctx: Ctx = { fy, onOpen, filter, open: shownOpen, toggle, focus };

  /* สายเปลี่ยน → จัดผังให้พอดีจอใหม่ (รอหนึ่งเฟรมให้ DOM วาดสายใหม่เสร็จก่อนวัด) */
  useEffect(() => {
    const id = requestAnimationFrame(() => fit());
    return () => cancelAnimationFrame(id);
  }, [focusId, fit]);
  const deputies = data.lanes.filter((l) => l.deputy).length;
  const depts = data.lanes.reduce((s, l) => s + l.departments.length, 0);

  return (
    <div className="opv-wrap">
      {/* ---------- แถบสถิติ + ตัวกรอง ----------
          ⚠ อยู่นอกพื้นที่เลื่อน — ผังกว้างกว่าจอ ถ้าเลื่อนไปขวาแล้วตัวกรองหายไปด้วย
            ผู้ใช้จะไม่รู้ว่าการ์ดที่จางอยู่จางเพราะอะไร */}
      <div className="opv-top">
        {/* ⚠ ปุ่มกรอง ทั้งหมด / ว่าง / ใกล้เกษียณ เอาออกแล้ว (ผู้ใช้สั่ง 19 ก.ย. 2569) — ยอดยังอยู่ทางขวา */}
        {/* ปุ่มกางทั้งหมด / ยุบถึงส่วนราชการ เอาออกแล้ว (ผู้ใช้สั่ง 19 ก.ย. 2569) — กาง/ยุบทั้งหมดอยู่ในชุดเครื่องมือมุมขวาบน
            ปุ่มกราฟคลื่นเกษียณอยู่มุมซ้ายของแถวบน — ผู้ใช้ย้ายกลับมาที่นี่ (19 ก.ย. 2569) เปิดแล้วขึ้นเป็นแผงข้าง */}
        {/* ปุ่มสลับไปผังหน่วยงาน — ผู้ใช้ขอ 19 ก.ย. 2569 · ผังหน่วยงานของบัญชีปลัดแสดงเฉพาะระดับ 9 ขึ้นไปอยู่แล้ว */}
        {onUnits && (
          <button
            type="button"
            className="icobtn"
            onClick={onUnits}
            aria-label="ดูผังหน่วยงาน (ระดับ 9 ขึ้นไป)"
            data-tip="ผังหน่วยงาน"
          >
            <ApartmentOutlined />
          </button>
        )}
        {onRetireWave && (
          <button
            type="button"
            className="icobtn"
            aria-haspopup="dialog"
            onClick={onRetireWave}
            aria-label="ดูกราฟคลื่นเกษียณของทั้งกระทรวง"
            data-tip="คลื่นเกษียณ 5 ปี"
          >
            <LineChartOutlined />
          </button>
        )}
        <dl className="opv-stats">
          <Stat k="ตำแหน่งระดับ 9 ขึ้นไป" v={stats.total} />
          <Stat k="ว่าง" v={stats.vacant} tone="alert" />
          {/* ⚠ แสดงเฉพาะผู้เกษียณปีงบนี้ (ผู้ใช้สั่ง 19 ก.ย. 2569) — ป้าย "ต.ค." คือเดือนที่อัตราว่างลง
                (เกษียณ 30 ก.ย. ตำแหน่งว่างตั้งแต่ 1 ต.ค.) ตามที่ผู้ใช้กำหนด */}
          <Stat k={`เกษียณ 1 ต.ค. ${fy}`} v={stats.thisYear} tone="warn" />
        </dl>
      </div>

      <div
        className={`opv-stage flow-canvas${dragging ? ' is-dragging' : ''}`}
        ref={box}
        role="region"
        aria-label="ผังผู้บริหารตามตำแหน่ง — ลากเพื่อเลื่อน ใช้ล้อเมาส์เพื่อซูม"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClickCapture={onClickCapture}
      >
      {/* --opv-lift = ขยายการ์ดที่ชี้เท่าไร — ชดเชยระดับที่ย่อผังไว้ ให้การ์ดที่ชี้อ่านได้ราวขนาดจริง
          ผู้ใช้ขอ 19 ก.ย. 2569: "hover ที่ card ให้ขยาย เพราะคลี่ทุกใบแล้วเล็ก"
          ⚠ ขยายให้ได้ราว 0.85 เท่าของขนาดจริง เพดาน 4 เท่า — ที่ซูมต่ำสุด (0.2) การ์ดกว้างราว 260px ยังไม่บังผังทั้งจอ */}
      <div
        className="opv"
        ref={pan}
        style={
          {
            transform: `translate(${view.x}px, ${view.y}px) scale(${view.k})`,
            '--opv-lift': String(Math.min(4, Math.max(1.06, 0.85 / view.k))),
          } as CSSProperties
        }
      >
      <div className="opv-tree">
        {data.palad && (
          <div className="opv-paladbox">
            <Palad c={data.palad} deputies={deputies} depts={depts} ctx={ctx} />
            <Toggle k="palad" ctx={ctx} n={all.length - 1} unit="ตำแหน่ง" vacant={stats.vacant - (data.palad.vacant ? 1 : 0)} dark />
          </div>
        )}

        {(shownOpen.has('palad') || !data.palad) && (
        <ul className="opv-branch opv-l1">
          {data.lanes.filter((lane) => !focus || focus.role === 'palad' || focus.lane === lane.missionGroup).map((lane) => {
            const tone = TONE[lane.missionGroup] ?? 'amber';
            const grouped = lane.missionGroup in TONE;
            const rows = lane.departments.flatMap(deptCards);
            return (
              <li key={lane.missionGroup} className={`opv-lane opv-tone-${tone}`}>
                <div className="opv-lhead">
                  <LaneTitle
                    /* ชื่อกลุ่มเต็มตามกฎกระทรวง (ผู้ใช้สั่ง 19 ก.ย. 2569) — ไม่ต้องมีหัวเล็กซ้ำคำว่า "กลุ่มภารกิจ" */
                    kicker=""
                    title={grouped ? lane.missionGroup : 'ส่วนราชการไม่สังกัดกลุ่มภารกิจ'}
                    n={rows.length}
                    vacant={rows.filter((c) => c.vacant).length}
                  />
                  {lane.deputy ? (
                    <button
                      type="button"
                      className={`opv-lperson${dim(lane.deputy, ctx) ? ' is-dim' : ''}${focus?.id === lane.deputy.posId ? ' is-sel' : ''}`}
                      onClick={() => onOpen(lane.deputy!.posId)}
                    >
                      <Avatar c={lane.deputy} size="lg" />
                      <span className="opv-who">
                        <span className="opv-k">รองปลัดกระทรวง{lane.deputy.assignment ? ` · ${lane.deputy.assignment}` : ''}</span>
                        <Name c={lane.deputy} big />
                        <Note c={lane.deputy} fy={fy} />
                      </span>
                    </button>
                  ) : (
                    /* ⚠ ข้อมูลไม่บอกว่ารองปลัดคนใดดูแลกลุ่มนี้ — บอกตรง ๆ ไม่ใช่เว้นว่างให้เดา */
                    <div className="opv-lperson is-unknown">
                      <span className="opv-who">
                        <span className="opv-k">รองปลัดกระทรวง</span>
                        <span className="opv-muted">ยังไม่มีข้อมูลว่าผู้ใดได้รับมอบหมาย</span>
                      </span>
                    </div>
                  )}
                  <Toggle
                    k={`lane:${lane.missionGroup}`}
                    ctx={ctx}
                    n={lane.departments.length}
                    unit="ส่วนราชการ"
                    vacant={rows.filter((c) => c.vacant).length - (lane.deputy?.vacant ? 1 : 0)}
                  />
                </div>
                {shownOpen.has(`lane:${lane.missionGroup}`) && (
                  <ul className="opv-branch opv-l2">
                    {lane.departments.filter((d) => !focus?.dept || focus.dept === d.name).map((d) => (
                      <li key={d.name}>
                        <Dept d={d} ctx={ctx} />
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}

          <StaffLane title="ที่ปรึกษา" tone="teal" list={advisers} ctx={ctx} label={(c) => c.title} />
          <StaffLane
            title="ผู้ตรวจราชการ"
            tone="gold"
            list={inspectors}
            ctx={ctx}
            label={(c) => c.assignment ?? 'ผู้ตรวจราชการกระทรวง'}
            highlight={(c) => c.assignment !== null}
          />
          <StaffLane title="ผู้ช่วยปลัด" tone="slate" list={assistants} ctx={ctx} label={() => 'ผู้ช่วยปลัดกระทรวง'} />
        </ul>
        )}

        {data.unassignedDeputies.length > 0 && (!focus || focus.role === 'palad' || focus.role === 'unassigned') && (
          <div className="opv-unassigned">
            <b>รองปลัดที่ยังไม่มีข้อมูลการมอบหมายกลุ่มภารกิจ</b>
            <div>
              {data.unassignedDeputies.map((c) => (
                <Card key={c.posId} c={c} label="รองปลัดกระทรวง" kind="staff" ctx={ctx} />
              ))}
            </div>
          </div>
        )}
      </div>
      </div>
      {/* เครื่องมือผัง — ชุดเดียวกับผังหน่วยงาน (ผู้ใช้ขอ 19 ก.ย. 2569) */}
      <div className="flow-tools" data-noexport="true">
        <ToolFan>
          <button type="button" className="icobtn" onClick={() => setOpen(new Set(allKeys))} aria-label="กางทั้งหมด" data-tip="กางทั้งหมด">
            <PlusSquareOutlined />
          </button>
          <button type="button" className="icobtn" onClick={() => setOpen(new Set(['palad']))} aria-label="ยุบทั้งหมด" data-tip="ยุบทั้งหมด">
            <MinusSquareOutlined />
          </button>
          <button
            type="button"
            className="icobtn"
            onClick={savePng}
            disabled={saving}
            aria-label={saving ? 'กำลังบันทึกรูปผัง' : 'บันทึกรูปผังทั้งผังเป็นไฟล์ PNG'}
            data-tip={saveErr ?? (saving ? 'กำลังบันทึก…' : 'บันทึกรูปผัง (PNG) — ได้ครบทุกกิ่งที่กางอยู่')}
          >
            {saving ? <LoadingOutlined /> : <CameraOutlined />}
          </button>
          {toolsExtra}
        </ToolFan>
      </div>
      <div className="flow-zoom">
        <button type="button" onClick={() => zoomAt(1.25)} aria-label="ซูมเข้า">+</button>
        <button type="button" onClick={fit} aria-label="จัดผังให้พอดีจอ">⌾</button>
        <button type="button" onClick={() => zoomAt(0.8)} aria-label="ซูมออก">−</button>
      </div>
      </div>
    </div>
  );
}

interface View {
  x: number;
  y: number;
  k: number;
}
const MIN_K = 0.2;
/** ขยับเกินกี่พิกเซลถึงนับว่าเป็นการลากผัง ไม่ใช่คลิก */
const DRAG_PX = 4;
const MAX_K = 1.6;
/** ⚠ ย่อพอดีจออัตโนมัติได้ไม่ต่ำกว่านี้ — ต่ำกว่านี้ชื่อบนการ์ดอ่านไม่ออก (ผู้ใช้ซูมออกเองได้ถึง MIN_K) */
const MIN_FIT = 0.6;
const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

/* ================================================================ */

interface Ctx {
  fy: number;
  onOpen: (posId: string) => void;
  filter: Filter;
  open: Set<string>;
  toggle: (key: string) => void;
  /** สายที่กำลังดู — null = ผังเต็ม */
  focus: Focus | null;
}

/** อัตราหนึ่งอยู่ตรงไหนของผัง — ใช้ตัดผังให้เหลือเฉพาะสาย */
interface Focus {
  id: string;
  role: 'palad' | 'laneDeputy' | 'deptHead' | 'deptDeputy' | 'deptAdviser' | 'unit' | 'staff' | 'unassigned';
  lane?: string;
  dept?: string;
  staff?: string;
}

function locate(data: ExecutivePositionsView, id: string): Focus | null {
  if (data.palad?.posId === id) return { id, role: 'palad' };
  for (const lane of data.lanes) {
    const L = lane.missionGroup;
    if (lane.deputy?.posId === id) return { id, role: 'laneDeputy', lane: L };
    for (const d of lane.departments) {
      const base = { id, lane: L, dept: d.name };
      if (d.head?.posId === id) return { ...base, role: 'deptHead' };
      if (d.deputies.some((c) => c.posId === id)) return { ...base, role: 'deptDeputy' };
      if (d.advisers.some((c) => c.posId === id)) return { ...base, role: 'deptAdviser' };
      if ([...d.directors, ...d.experts].some((c) => c.posId === id)) return { ...base, role: 'unit' };
    }
  }
  const staff: [string, ExecutivePositionCard[]][] = [
    ['ที่ปรึกษา', data.staff.advisers],
    ['ผู้ตรวจราชการ', data.staff.inspectors],
    ['ผู้ช่วยปลัด', data.staff.assistants],
  ];
  for (const [t, list] of staff) if (list.some((c) => c.posId === id)) return { id, role: 'staff', staff: t };
  if (data.unassignedDeputies.some((c) => c.posId === id)) return { id, role: 'unassigned' };
  return null;
}

/**
 * ปุ่มกาง/ยุบ — หน้าตาเดียวกับ .ncard-toggle ของผังหน่วยงาน (เครื่องหมาย + / − ในวงกลม แล้วตามด้วยจำนวน)
 * ⚠ ต้องอยู่นอกปุ่มการ์ด ไม่ใช่ข้างใน — ปุ่มซ้อนในปุ่มเป็น HTML ที่ผิด (เหตุผลเดียวกับใน OrgFlow.tsx)
 */
function Toggle({ k, ctx, n, unit, vacant, dark }: { k: string; ctx: Ctx; n: number; unit: string; vacant: number; dark?: boolean }) {
  const isOpen = ctx.open.has(k);
  if (n === 0) return null;
  return (
    <button
      type="button"
      className={`opv-toggle${dark ? ' is-dark' : ''}`}
      aria-expanded={isOpen}
      onClick={() => ctx.toggle(k)}
    >
      <span className="sign" aria-hidden="true">{isOpen ? '−' : '+'}</span>
      <span className="cnt">
        {isOpen ? 'ยุบ' : 'กางดู'} {n.toLocaleString('th-TH')} {unit}
      </span>
      {vacant > 0 && <span className="vac">ว่าง {vacant.toLocaleString('th-TH')}</span>}
    </button>
  );
}

function allCards(v: ExecutivePositionsView): ExecutivePositionCard[] {
  const out: ExecutivePositionCard[] = [];
  if (v.palad) out.push(v.palad);
  out.push(...v.staff.advisers, ...v.staff.inspectors, ...v.staff.assistants, ...v.unassignedDeputies);
  for (const l of v.lanes) {
    if (l.deputy) out.push(l.deputy);
    for (const d of l.departments) out.push(...deptCards(d));
  }
  return out;
}

function deptCards(d: ExecutivePositionDept): ExecutivePositionCard[] {
  return [...(d.head ? [d.head] : []), ...d.deputies, ...d.advisers, ...d.directors, ...d.experts];
}


/** การ์ดไม่ตรงตัวกรอง → จางลง */
function dim(c: ExecutivePositionCard, ctx: Ctx): boolean {
  if (ctx.filter === 'vacant') return !c.vacant;
  if (ctx.filter === 'retiring') return c.vacant || c.retireFiscalYear !== ctx.fy;
  return false;
}

function Stat({ k, v, tone }: { k: string; v: number; tone?: 'alert' | 'warn' }) {
  return (
    <div className={tone ? `is-${tone}` : undefined}>
      <dd>{v.toLocaleString('th-TH')}</dd>
      <dt>{k}</dt>
    </div>
  );
}

function Palad({ c, deputies, depts, ctx }: { c: ExecutivePositionCard; deputies: number; depts: number; ctx: Ctx }) {
  return (
    <button type="button" className={`opv-palad${dim(c, ctx) ? ' is-dim' : ''}${ctx.focus?.id === c.posId ? ' is-sel' : ''}`} onClick={() => ctx.onOpen(c.posId)}>
      <span className="opv-palad-av">{c.vacant ? '–' : initial(c.name)}</span>
      <span className="opv-palad-k">ปลัดกระทรวงการคลัง</span>
      <span className="opv-palad-n">{c.vacant ? 'ว่าง' : c.name}</span>
      <span className="opv-palad-tags">
        <span>{deputies} รองปลัด</span>
        <span>{depts} ส่วนราชการ</span>
      </span>
    </button>
  );
}

function LaneTitle({ kicker, title, n, vacant }: { kicker: string; title: string; n: number; vacant: number }) {
  return (
    <div className="opv-lt">
      <div className="opv-lt-a">
        {kicker && <span className="opv-lt-k">{kicker}</span>}
        <span className="opv-lt-t">{title}</span>
      </div>
      <div className="opv-lt-n">
        <b>{n.toLocaleString('th-TH')}</b>
        {/* คำว่า "อัตรา · ว่าง N" ใต้ตัวเลขเอาออก (ผู้ใช้สั่ง 19 ก.ย. 2569) — ยอดว่างยังอยู่ที่ปุ่มกาง/ยุบของกิ่ง */}
      </div>
    </div>
  );
}

function StaffLane({
  title,
  tone,
  list,
  ctx,
  label,
  highlight,
}: {
  title: string;
  tone: string;
  list: ExecutivePositionCard[];
  ctx: Ctx;
  label: (c: ExecutivePositionCard) => string;
  highlight?: (c: ExecutivePositionCard) => boolean;
}) {
  /* โหมดดูเฉพาะสาย — กิ่งอื่นซ่อน กิ่งของคนนั้นเหลือการ์ดเดียว */
  const f = ctx.focus;
  if (f && f.role !== 'palad') {
    if (f.staff !== title) return null;
    list = list.filter((c) => c.posId === f.id);
  }
  if (list.length === 0) return null;
  /* กิ่งที่มีคนเดียว (ผู้ช่วยปลัด) — แสดงตัวคนในหัวกิ่งเลย ไม่ต้องกดกาง (ผู้ใช้สั่ง 19 ก.ย. 2569) */
  if (list.length === 1) {
    const c = list[0]!;
    return (
      <li className={`opv-lane opv-staff opv-tone-${tone}`}>
        <div className="opv-lhead">
          <LaneTitle kicker="ขึ้นตรงปลัดกระทรวง" title={title} n={1} vacant={c.vacant ? 1 : 0} />
          <button
            type="button"
            className={`opv-lperson${c.vacant ? ' is-vacant' : ''}${dim(c, ctx) ? ' is-dim' : ''}${ctx.focus?.id === c.posId ? ' is-sel' : ''}`}
            onClick={() => ctx.onOpen(c.posId)}
          >
            <Avatar c={c} size="lg" />
            <span className="opv-who">
              <span className="opv-k">{label(c)}</span>
              <Name c={c} big />
              <Note c={c} fy={ctx.fy} />
            </span>
          </button>
        </div>
      </li>
    );
  }
  return (
    <li className={`opv-lane opv-staff opv-tone-${tone}`}>
      <div className="opv-lhead">
        <LaneTitle kicker="ขึ้นตรงปลัดกระทรวง" title={title} n={list.length} vacant={list.filter((c) => c.vacant).length} />
        <Toggle k={`staff:${title}`} ctx={ctx} n={list.length} unit="ตำแหน่ง" vacant={list.filter((c) => c.vacant).length} />
      </div>
      {ctx.open.has(`staff:${title}`) && (
        <ol className="opv-rail">
          {list.map((c) => (
            <li key={c.posId}>
              <Card c={c} label={label(c)} kind="staff" ctx={ctx} hl={highlight?.(c)} />
            </li>
          ))}
        </ol>
      )}
    </li>
  );
}

function Dept({ d, ctx }: { d: ExecutivePositionDept; ctx: Ctx }) {
  const h = d.head;
  const n = deptCards(d);
  const vacant = n.filter((c) => c.vacant).length;
  const name = d.name.replace(' กระทรวงการคลัง', '');
  return (
    <div className="opv-dept">
      <div className="opv-dhead">
        <div className="opv-dhead-t">
          <span className="opv-k">
            {d.short && d.short !== name ? `${d.short} · ` : ''}
            {n.length.toLocaleString('th-TH')} อัตรา
            {vacant > 0 && <b className="is-alert"> · ว่าง {vacant}</b>}
          </span>
          <span className="opv-dname">{name}</span>
        </div>
        {h ? (
          <button type="button" className={`opv-dperson${dim(h, ctx) ? ' is-dim' : ''}${ctx.focus?.id === h.posId ? ' is-sel' : ''}`} onClick={() => ctx.onOpen(h.posId)}>
            <Avatar c={h} size="lg" />
            <span className="opv-who">
              <span className="opv-k">{h.title.startsWith('อธิบดี') ? 'อธิบดี' : 'ผู้อำนวยการ'}</span>
              <Name c={h} big />
              <Note c={h} fy={ctx.fy} />
            </span>
          </button>
        ) : (
          /* ส่วนราชการที่ไม่มีหัวระดับบริหารสูงของตัวเอง — สป.กค. (หัวคือปลัด) และสำนักงานรัฐมนตรี */
          <div className="opv-dperson is-plain">
            {d.name === 'สำนักงานปลัดกระทรวงการคลัง' ? 'หัวส่วนราชการคือปลัดกระทรวง' : 'ไม่มีอัตราหัวส่วนราชการระดับ 9 ขึ้นไป'}
          </div>
        )}
        <Toggle
          k={`dept:${d.name}`}
          ctx={ctx}
          n={n.length - (h ? 1 : 0)}
          unit="ตำแหน่ง"
          vacant={vacant - (h?.vacant ? 1 : 0)}
        />
      </div>

      {n.length > (h ? 1 : 0) && ctx.open.has(`dept:${d.name}`) && (
        /* อธิบดี ─┬ รองอธิบดี ─┬ ผอ. สำนัก/กอง ทุกคน + ผู้เชี่ยวชาญ — แนวนอนแถวเดียวกัน
                   └ ที่ปรึกษา
           ตามภาพร่างของผู้ใช้ 19 ก.ย. 2569 — ผอ. กับ ชช ระดับเดียวกัน · ผอ. สำนักเรียงระดับเดียวกัน */
        <ul className="opv-branch opv-dtree">
          {/* โหมดดูเฉพาะสาย: เปิดหัวส่วนราชการ = เห็นลูกทั้งหมดตามปกติ · เปิดรอง = ซ่อนที่ปรึกษา
              เปิดที่ปรึกษา = ซ่อนสายรอง · เปิด ผอ./ชช = เหลือรองกับการ์ดใบนั้น */}
          {ctx.focus?.role === 'deptAdviser' ? null : d.deputies.length === 0 ? (
            /* ไม่มีรอง (สป.กค. · สำนักงานรัฐมนตรี) — ผอ. กับ ชช ขึ้นตรงหัวส่วนราชการ */
            <UnitHeads d={d} ctx={ctx} />
          ) : (
            <li>
              <div className={`opv-dnode${d.deputies.length > 1 ? ' is-group' : ''}`}>
                {d.deputies.map((c) => (
                  <Card key={c.posId} c={c} label={c.title} kind="deputy" ctx={ctx} />
                ))}
                {/* ⚠ ข้อมูลไม่มีฟิลด์ผู้บังคับบัญชา — รองคนเดียวจึงวาง ผอ./ชช ไว้ใต้รองได้ตรง ๆ
                      ถ้ามีรองหลายคน วางไว้ใต้ "กลุ่มรอง" รวมกัน ห้ามเดาแบ่งให้รองแต่ละคน */}
                {d.deputies.length > 1 && <p className="opv-dgroup-note">ข้อมูลไม่ระบุว่าหน่วยใดขึ้นกับรองคนใด</p>}
              </div>
              {d.directors.length + d.experts.length > 0 && (
                <ul className="opv-branch opv-dtree">
                  <UnitHeads d={d} ctx={ctx} />
                </ul>
              )}
            </li>
          )}
          {/* ที่ปรึกษา (ทรงคุณวุฒิ) ขึ้นตรงหัวส่วนราชการ — ระดับเดียวกับรอง */}
          {d.advisers.filter((c) => !deptFocused(ctx) || ctx.focus!.id === c.posId).map((c) => (
            <li key={c.posId}>
              <Card c={c} label={c.title} kind="adviser" ctx={ctx} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * ชั้นใต้รองหัวส่วนราชการ — ผู้อำนวยการกับผู้เชี่ยวชาญ (ชช) อยู่ระดับเดียวกัน (ซี 9 ทั้งคู่)
 * ผู้ใช้สั่ง 19 ก.ย. 2569: "อธิบดี → รองอธิบดี → ผู้อำนวยการ กับ ชช ระดับเดียวกัน"
 *
 * ⚠ ผอ. ใช้ชื่อหน่วยเป็นหัวการ์ด — ชื่อตำแหน่งในทะเบียนซ้ำกันหมด ("ผู้อำนวยการ")
 *   หน่วย "ส่วนกลาง" ไม่บอกอะไร จึงถอยไปใช้ชื่อตำแหน่งแทน (เช่น หัวหน้าสำนักงาน)
 * ⚠ ผอ. กับ ชช คนละตำแหน่ง (ผู้ใช้ย้ำ 19 ก.ย. 2569) — ผอ. = อำนวยการสูง · ชช = เชี่ยวชาญ
 */
function UnitHeads({ d, ctx }: { d: ExecutivePositionDept; ctx: Ctx }) {
  /* ⚠ ผอ. ทุกสำนัก/กองเรียงแนวนอนแถวเดียวกัน (ผู้ใช้สั่ง 19 ก.ย. 2569 "ผอ สำนัก ต้องเรียงระดับเดียวกัน")
       ห้ามซ้อนเป็นคอลัมน์แนวตั้ง — อ่านแล้วเหมือน ผอ. อยู่ใต้กันเป็นลำดับ
       ผู้เชี่ยวชาญต่อท้ายในแถวเดียวกัน (ระดับ 9 เท่ากัน) แยกด้วยสีการ์ด */
  return (
    <>
      {d.directors.filter((c) => ctx.focus?.role !== 'unit' || ctx.focus.id === c.posId).map((c) => (
        <li key={c.posId}>
          <Card c={c} label={directorLabel(c)} kind="director" ctx={ctx} />
        </li>
      ))}
      {d.experts.filter((c) => ctx.focus?.role !== 'unit' || ctx.focus.id === c.posId).map((c) => (
        <li key={c.posId}>
          <Card c={c} label={c.title} kind="expert" ctx={ctx} />
        </li>
      ))}
    </>
  );
}

/**
 * ชื่อตำแหน่งเต็มของ ผอ. — ผู้ใช้สั่ง 19 ก.ย. 2569 "เอาชื่อตำแหน่งแสดงด้วย"
 *
 * ในข้อมูล ชื่อตำแหน่งของ ผอ. ส่วนใหญ่เป็นคำว่า "ผู้อำนวยการ" เฉย ๆ (66 จาก 75 อัตรา)
 * ส่วนชื่อหน่วยอยู่แยกอีกช่อง จึงประกอบเป็น "ผู้อำนวยการ" + ชื่อหน่วย เช่น ผู้อำนวยการสำนักกฎหมาย
 *   "ผู้อำนวยการศูนย์เทคโนโลยีสารสนเทศ" @ ศูนย์เทคโนโลยีสารสนเทศ → ไม่ซ้ำคำ (หน่วยขึ้นต้นด้วยท่อนหลังของชื่อ)
 *   ส่วนกลาง → ใช้ชื่อตำแหน่งตามทะเบียน เพราะ "ผู้อำนวยการส่วนกลาง" ไม่มีจริง
 *   ชื่ออื่น (เช่น หัวหน้าสำนักงาน) → ชื่อตำแหน่ง ตามด้วยหน่วยถ้ายังไม่มีอยู่ในชื่อ
 */
function directorLabel(c: ExecutivePositionCard): string {
  const title = c.title.trim();
  const unit = (c.unit ?? '').trim();
  if (!unit || unit === 'ส่วนกลาง' || title.includes(unit)) return title;
  const DIR = 'ผู้อำนวยการ';
  if (title.startsWith(DIR) && unit.startsWith(title.slice(DIR.length))) return DIR + unit;
  return `${title} ${unit}`;
}

type Kind = 'deputy' | 'adviser' | 'director' | 'expert' | 'staff';

function Card({
  c,
  label,
  kind,
  ctx,
  hl,
}: {
  c: ExecutivePositionCard;
  label: string;
  kind: Kind;
  ctx: Ctx;
  hl?: boolean;
}) {
  return (
    <button
      type="button"
      className={`opv-card is-${kind}${c.vacant ? ' is-vacant' : ''}${hl ? ' is-hl' : ''}${ctx.focus?.id === c.posId ? ' is-sel' : ''}${dim(c, ctx) ? ' is-dim' : ''}`}
      onClick={() => ctx.onOpen(c.posId)}
    >
      <span className="opv-card-l">{label}</span>
      <span className="opv-card-b">
        <Avatar c={c} />
        <span className="opv-who">
          <Name c={c} />
          <Note c={c} fy={ctx.fy} />
        </span>
      </span>
    </button>
  );
}

/**
 * อักษรแรกของชื่อ (ไม่รวมคำนำหน้า)
 *
 * ⚠ ข้ามสระหน้า เ แ โ ใ ไ — ไม่งั้น "เกษราภรณ์" ได้วงกลมที่มีแต่ "เ" ซึ่งอ่านไม่ออก
 */
function initial(name: string | null): string {
  if (!name) return '–';
  const first = name.replace(/^(ว่าที่ร้อย(ตรี|โท|เอก)(หญิง)?|นางสาว|นาย|นาง)/, '').trim();
  const ch = first.charAt(0);
  return 'เแโใไ'.includes(ch) ? first.charAt(1) || ch : ch;
}

function Avatar({ c, size }: { c: ExecutivePositionCard; size?: 'lg' }) {
  return (
    <span className={`opv-av${size === 'lg' ? ' is-lg' : ''}${c.vacant ? ' is-vacant' : ''}`} aria-hidden="true">
      {c.vacant ? <i /> : initial(c.name)}
    </span>
  );
}

function Name({ c, big }: { c: ExecutivePositionCard; big?: boolean }) {
  return (
    <span className={`opv-name${big ? ' is-big' : ''}${c.vacant ? ' is-vacant' : ''}`}>{c.vacant ? 'ว่าง' : c.name}</span>
  );
}

/**
 * บรรทัดใต้ชื่อ — วันเกษียณของทุกคน (ปีงบนี้เน้นสี)
 *
 * ⚠ ใช้ปีงบที่เกษียณ ไม่ใช่ retireInYears — ข้าราชการเกษียณ 30 ก.ย. ของปีงบที่อายุครบ 60
 */
function Note({ c, fy }: { c: ExecutivePositionCard; fy: number }) {
  /* ⚠ อัตราว่างขึ้นคำว่า "ว่าง" คำเดียว ไม่แสดงผู้รักษาการ (ผู้ใช้สั่ง 19 ก.ย. 2569) */
  if (c.vacant) return null;
  /* ทุกคนมีวันเกษียณบนการ์ด (ผู้ใช้สั่ง 19 ก.ย. 2569) — ปีงบนี้สีเตือน ปีอื่นสีจาง
     "1 ต.ค. ปีงบที่เกษียณ" คือวันที่อัตราว่างลง ตามที่ผู้ใช้กำหนด */
  if (c.retireFiscalYear === null) return null;
  return (
    <span className={`opv-note${c.retireFiscalYear === fy ? ' is-warn' : ' is-soft'}`}>เกษียณ 1 ต.ค. {c.retireFiscalYear}</span>
  );
}

/** ในโหมดดูเฉพาะสาย ตัวที่เลือกอยู่ลึกกว่าหัวส่วนราชการหรือไม่ — ถ้าใช่ พี่น้องชั้นรอง/ที่ปรึกษาต้องซ่อน */
function deptFocused(ctx: Ctx): boolean {
  const r = ctx.focus?.role;
  return r === 'deptDeputy' || r === 'deptAdviser' || r === 'unit';
}
